import { Box, Stack, Typography, useTheme, alpha } from "@mui/material";
import { colors, shadows } from "../source/source.styles";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AssignmentIcon from "@mui/icons-material/Assignment";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import StarIcon from "@mui/icons-material/Star";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import VerifiedIcon from "@mui/icons-material/Verified";
import SearchIcon from "@mui/icons-material/Search";
import EventIcon from "@mui/icons-material/Event";
import ForumIcon from "@mui/icons-material/Forum";
import DescriptionIcon from "@mui/icons-material/Description";
import HandshakeIcon from "@mui/icons-material/Handshake";
import MedicationIcon from "@mui/icons-material/Medication";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

const DSPConversionFunnel = ({ data }) => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;

  // Aggregate pipeline stages from all sources
  const sources = data?.sources || [];

  // Calculate totals for each stage
  const stageTotals = {
    new: sources.reduce((sum, s) => sum + (s.newLeads || 0), 0),
    hot: sources.reduce((sum, s) => sum + (s.hotLeads || 0), 0),
    warm: sources.reduce((sum, s) => sum + (s.warmLeads || 0), 0),
    cold: sources.reduce((sum, s) => sum + (s.coldLeads || 0), 0),
    qualified: sources.reduce((sum, s) => sum + (s.qualifiedLeads || 0), 0),
    needAnalysis: sources.reduce((sum, s) => sum + (s.needAnalysisLeads || 0), 0),
    booked: sources.reduce((sum, s) => sum + (s.bookedLeads || 0), 0),
    postConsult: sources.reduce((sum, s) => sum + (s.postConsultLeads || 0), 0),
    proposal: sources.reduce((sum, s) => sum + (s.proposalSharedLeads || 0), 0),
    negotiation: sources.reduce((sum, s) => sum + (s.negotiationLeads || 0), 0),
    treatmentBooked: sources.reduce((sum, s) => sum + (s.treatmentBookedLeads || 0), 0),
    won: sources.reduce((sum, s) => sum + (s.wonLeads || 0), 0),
  };

  const totalLeads = data?.summary?.totalLeads || 0;
  const conversionRate = totalLeads > 0 ? Math.round((stageTotals.won / totalLeads) * 1000) / 10 : 0;

  // Calculate others (dropped, unknown statuses)
  const knownTotal = Object.values(stageTotals).reduce((sum, val) => sum + val, 0);
  const others = totalLeads - knownTotal;

  // Define all stages with icons and colors
  const allStages = [
    { key: "new", label: "New Leads", color: "#3B82F6", gradient: ["#3B82F6", "#60A5FA"], icon: <AssignmentIcon /> },
    { key: "hot", label: "Hot Leads", color: "#EF4444", gradient: ["#EF4444", "#F87171"], icon: <LocalFireDepartmentIcon /> },
    { key: "warm", label: "Warm Leads", color: "#F59E0B", gradient: ["#F59E0B", "#FBBF24"], icon: <StarIcon /> },
    { key: "cold", label: "Cold Leads", color: "#6B7280", gradient: ["#6B7280", "#9CA3AF"], icon: <AcUnitIcon /> },
    { key: "qualified", label: "Qualified", color: "#10B981", gradient: ["#10B981", "#34D399"], icon: <VerifiedIcon /> },
    { key: "needAnalysis", label: "Need Analysis", color: "#F97316", gradient: ["#F97316", "#FB923C"], icon: <SearchIcon /> },
    { key: "booked", label: "Booked", color: "#8B5CF6", gradient: ["#8B5CF6", "#A78BFA"], icon: <EventIcon /> },
    { key: "postConsult", label: "Post Consult", color: "#6366F1", gradient: ["#6366F1", "#818CF8"], icon: <ForumIcon /> },
    { key: "proposal", label: "Proposal", color: "#A855F7", gradient: ["#A855F7", "#C084FC"], icon: <DescriptionIcon /> },
    { key: "negotiation", label: "Negotiation", color: "#EC4899", gradient: ["#EC4899", "#F472B6"], icon: <HandshakeIcon /> },
    { key: "treatmentBooked", label: "Treatment", color: "#14B8A6", gradient: ["#14B8A6", "#2DD4BF"], icon: <MedicationIcon /> },
    { key: "won", label: "Won Deals", color: "#059669", gradient: ["#059669", "#10B981"], icon: <EmojiEventsIcon /> },
    { key: "others", label: "Others", color: "#9CA3AF", gradient: ["#9CA3AF", "#D1D5DB"], icon: <HelpOutlineIcon /> },
  ];

  // Add counts and calculate percentages
  const stagesWithData = allStages.map(stage => ({
    ...stage,
    count: stage.key === "others" ? others : (stageTotals[stage.key] || 0),
    percentage: totalLeads > 0 ? Math.round(((stage.key === "others" ? others : stageTotals[stage.key]) / totalLeads) * 1000) / 10 : 0,
  }));

  // Sort by count and take top 3
  const topStages = stagesWithData
    .filter(s => s.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: 3,
        p: 3,
        border: `1px solid ${colors.borderLight}`,
        boxShadow: shadows.card,
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 700,
              color: colors.textPrimary,
              mb: 0.5,
            }}
          >
            Pipeline Funnel
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              color: colors.textSecondary,
            }}
          >
            Top performing stages
          </Typography>
        </Box>
        <Box
          sx={{
            bgcolor: `${colors.success}15`,
            px: 2,
            py: 1,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <TrendingUpIcon sx={{ fontSize: 16, color: colors.success }} />
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 700,
              color: colors.success,
            }}
          >
            {conversionRate}%
          </Typography>
        </Box>
      </Stack>

      {/* Funnel Stages */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
        {topStages.map((stage, index) => (
          <Box
            key={stage.key}
            sx={{
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Background bar */}
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                height: "100%",
                width: "100%",
                bgcolor: alpha(stage.color, 0.08),
                borderRadius: 2,
              }}
            />

            {/* Content */}
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                py: 2,
                px: 2,
              }}
            >
              {/* Icon Badge */}
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: stage.gradient[0],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                  boxShadow: `0 4px 12px ${alpha(stage.color, 0.3)}`,
                  color: "#FFFFFF",
                }}
              >
                {stage.icon}
              </Box>

              {/* Stage Info */}
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: colors.textPrimary,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    {stage.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 22,
                      fontWeight: 700,
                      color: stage.color,
                    }}
                  >
                    {stage.count.toLocaleString()}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={2}>
                  {/* Progress bar */}
                  <Box
                    sx={{
                      flex: 1,
                      height: 10,
                      bgcolor: alpha("#E5E7EB", 1),
                      borderRadius: 5,
                      overflow: "hidden",
                      position: "relative",
                    }}
                  >
                    {/* Animated progress bar with percentage-based colors */}
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        height: "100%",
                        width: `${stage.percentage}%`,
                        background: `linear-gradient(90deg,
                          ${stage.percentage >= 70 ? "#10B981" : stage.percentage >= 40 ? "#F59E0B" : "#EF4444"} 0%,
                          ${stage.percentage >= 70 ? "#34D399" : stage.percentage >= 40 ? "#FBBF24" : "#F87171"} 50%,
                          ${stage.percentage >= 70 ? "#10B981" : stage.percentage >= 40 ? "#F59E0B" : "#EF4444"} 100%)`,
                        backgroundSize: "200% 100%",
                        borderRadius: 5,
                        animation: "shimmer 2s infinite linear",
                        "@keyframes shimmer": {
                          "0%": { backgroundPosition: "200% 0" },
                          "100%": { backgroundPosition: "-200% 0" },
                        },
                        transition: "width 0.8s ease-out, background 0.3s ease",
                        boxShadow: `0 0 12px ${alpha(
                          stage.percentage >= 70 ? "#10B981" : stage.percentage >= 40 ? "#F59E0B" : "#EF4444",
                          0.6
                        )}`,
                      }}
                    />
                    {/* Glow effect */}
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        height: "100%",
                        width: `${stage.percentage}%`,
                        background: `linear-gradient(90deg,
                          transparent 0%,
                          ${alpha(
                            stage.percentage >= 70 ? "#10B981" : stage.percentage >= 40 ? "#F59E0B" : "#EF4444",
                            0.4
                          )} 50%,
                          transparent 100%)`,
                        borderRadius: 5,
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: stage.percentage >= 70 ? "#10B981" : stage.percentage >= 40 ? "#F59E0B" : "#EF4444",
                      minWidth: 55,
                      textAlign: "right",
                    }}
                  >
                    {stage.percentage}%
                  </Typography>
                </Stack>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Footer Summary */}
      <Box
        sx={{
          mt: 2,
          pt: 2,
          borderTop: `1px solid ${colors.borderLight}`,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckCircleIcon sx={{ fontSize: 16, color: colors.success }} />
            <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
              Total in Top Stages
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>
            {topStages.reduce((sum, s) => sum + s.count, 0).toLocaleString()}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export default DSPConversionFunnel;
